import { store } from '../store.js';
import {
  saveAppState,
  loadAppState,
  backfillArchivedMatches
} from '../storage.js';
import {
  SCHEMA_VERSION,
  migrateState,
  getDefaultCurrentPoint,
  stripPlayerRef,
  stripLineup,
  stripPoint,
  stripSet,
  compactArchivedState
} from './useMigration.js';

/**
 * Persistence composable — handles localStorage save/load/watchers/archive backfill.
 * Bridge pattern: receives component instance for $watch during Options API phase.
 */
export function usePersistence(instance) {
  function buildPersistedState() {
    const isFinished = Boolean(store.match.finished);
    const matchPayload = {
      ...store.match,
      sets: Array.isArray(store.match.sets)
        ? store.match.sets.map(stripSet)
        : []
    };
    return {
      schemaVersion: SCHEMA_VERSION,
      store: {
        match: matchPayload,
        currentLineup: stripLineup(store.currentLineup),
        currentLibero: stripPlayerRef(store.currentLibero)
      },
      app: {
        matchReady: instance.matchReady,
        startingPlayersConfirmed: instance.startingPlayersConfirmed,
        matchStartServeReceive: instance.matchStartServeReceive,
        matchTiebreakServeReceive: instance.matchTiebreakServeReceive,
        score: instance.score,
        currentPoint: isFinished ? getDefaultCurrentPoint() : stripPoint(instance.currentPoint),
        startPointDisabled: isFinished ? true : instance.startPointDisabled
      }
    };
  }

  function persistState() {
    saveAppState(buildPersistedState());
  }

  function loadPersistedState() {
    const saved = migrateState(loadAppState());
    if (!saved) {
      return;
    }
    if (saved.schemaVersion !== SCHEMA_VERSION) {
      console.warn('Ignoring saved app state due to schema version mismatch.');
      return;
    }
    if (saved.store) {
      store.hydrateFromStorage(saved.store);
    }
    if (saved.app) {
      instance.matchReady = Boolean(saved.app.matchReady);
      instance.startingPlayersConfirmed = Boolean(saved.app.startingPlayersConfirmed);
      instance.matchStartServeReceive = saved.app.matchStartServeReceive || '';
      instance.matchTiebreakServeReceive = saved.app.matchTiebreakServeReceive || '';
      instance.score = saved.app.score || { won: 0, lost: 0 };
      if (store.match.finished) {
        instance.currentPoint = getDefaultCurrentPoint();
        instance.startPointDisabled = true;
      } else {
        const rawPoint = saved.app.currentPoint || getDefaultCurrentPoint();
        instance.currentPoint = store.hydratePointReferences(rawPoint);
        instance.startPointDisabled = Boolean(saved.app.startPointDisabled);
      }
    } else {
      instance.matchReady = store.match.roster.length > 0;
      instance.startingPlayersConfirmed = false;
      instance.matchStartServeReceive = '';
      instance.matchTiebreakServeReceive = '';
      instance.score = { won: 0, lost: 0 };
      instance.currentPoint = getDefaultCurrentPoint();
      instance.startPointDisabled = false;
    }

    instance.matchRoster = store.match.roster;
    instance.matchLiberos = instance.matchRoster.filter(player => player && player.playerType === 'libero');
    instance.currentLineup = store.currentLineup.slice();

    if (instance.startingPlayersConfirmed && !instance.currentPoint.startPoint) {
      instance.startPointDisabled = false;
    }

    if (instance.startingPlayersConfirmed) {
      if (!Array.isArray(instance.currentPoint.currentLineupNumbers) || instance.currentPoint.currentLineupNumbers.length < 6) {
        instance.currentPoint.currentLineupNumbers = instance.currentLineup.map(player => player.player.number);
      }
      instance.currentPoint.currentOnCourtNumbers = instance.applyLiberoSubstitution(instance.currentPoint.currentLineupNumbers);
    }

    const currentSet = store.getCurrentSet();
    if (currentSet && currentSet.finished && !store.match.finished) {
      instance.score = { ...currentSet.score };
      instance.currentPoint = getDefaultCurrentPoint();
      instance.currentPoint.currentScore = { ...currentSet.score };
      instance.startPointDisabled = true;
      instance.showSetSummary = true;
    }
  }

  function setupPersistenceWatchers() {
    instance.$watch(() => store.match, persistState, { deep: true });
    instance.$watch(() => store.currentLineup, persistState, { deep: true });
    instance.$watch(() => store.currentLibero, persistState);
    instance.$watch(() => instance.currentPoint, persistState, { deep: true });
    instance.$watch(() => instance.score, persistState, { deep: true });
    instance.$watch(() => instance.matchReady, persistState);
    instance.$watch(() => instance.startingPlayersConfirmed, persistState);
    instance.$watch(() => instance.matchStartServeReceive, persistState);
    instance.$watch(() => instance.matchTiebreakServeReceive, persistState);
    instance.$watch(() => instance.startPointDisabled, persistState);

    // Auto-confirm when all required fields are complete
    instance.$watch(() => instance.isConfirmDisabled, (newValue, oldValue) => {
      if (oldValue === true && newValue === false) {
        instance.autoConfirm();
      }
    });
  }

  function backfillArchivedMatchesData() {
    const flagKey = 'moss-side-scoring:archives:compacted';
    if (localStorage.getItem(flagKey) === 'true') {
      return;
    }
    const updated = backfillArchivedMatches(compactArchivedState);
    if (updated > 0) {
      localStorage.setItem(flagKey, 'true');
    }
  }

  return {
    buildPersistedState,
    persistState,
    loadPersistedState,
    setupPersistenceWatchers,
    backfillArchivedMatchesData
  };
}
